import type { TaxonomyTerm } from "@/types/taxonomy";

export type StockStatus = "IN_STOCK" | "LOW_STOCK" | "OUT_OF_STOCK" | "DISCONTINUED";
export type StoreSection =
  | "PRODUCE"
  | "BAKERY"
  | "REFRIGERATED"
  | "DAIRY"
  | "MEAT"
  | "FROZEN"
  | "PANTRY"
  | "BEVERAGES"
  | "SNACKS"
  | "HOUSEHOLD"
  | "FRONT"
  | "OTHER";

export interface ProductSummary {
  id: string;
  name: string;
  slug: string;
  brand: string | null;
  price: number | null;
  unit: string | null;
  photoUrl: string | null;
  photoPosition?: string;
  stockStatus: StockStatus;
  storeSection: StoreSection;
  allergyTypes: TaxonomyTerm[];
  dietTypes: TaxonomyTerm[];
  categories: TaxonomyTerm[];
  sortOrder: number;
}

export interface ProductDetail extends ProductSummary {
  description: string | null;
  metaTitle: string | null;
  metaDescription: string | null;
  ogImageUrl: string | null;
  canonicalUrl: string | null;
  published: boolean;
  // Inventory
  stockQuantity?: number | null;
  trackInventory?: boolean;
  lowStockThreshold?: number;
  // Fulfillment
  shippingFlatRate?: number | null;
  pickupOnly?: boolean;
}

export interface ProductRequest {
  name: string;
  slug: string;
  brand?: string;
  description?: string;
  price?: number;
  unit?: string;
  photoUrl?: string;
  photoPosition?: string;
  stockStatus: StockStatus;
  storeSection: StoreSection;
  metaTitle?: string;
  metaDescription?: string;
  ogImageUrl?: string;
  canonicalUrl?: string;
  published: boolean;
  sortOrder: number;
  // Inventory
  stockQuantity?: number | null;
  trackInventory?: boolean;
  lowStockThreshold?: number;
  // Fulfillment
  shippingFlatRate?: number | null;
  pickupOnly?: boolean;
  termIds: string[];
}

export interface ProductFilters {
  allergyType?: string;
  dietType?: string;
  category?: string;
  storeSection?: StoreSection;
  stockStatus?: StockStatus;
  q?: string;
}

export const STOCK_STATUS_LABELS: Record<StockStatus, string> = {
  IN_STOCK: "In stock",
  LOW_STOCK: "Low stock",
  OUT_OF_STOCK: "Out of stock",
  DISCONTINUED: "Discontinued",
};

export const STORE_SECTION_LABELS: Record<StoreSection, string> = {
  PRODUCE: "Produce",
  BAKERY: "Bakery",
  REFRIGERATED: "Refrigerated",
  DAIRY: "Dairy",
  MEAT: "Meat",
  FROZEN: "Frozen",
  PANTRY: "Pantry",
  BEVERAGES: "Beverages",
  SNACKS: "Snacks",
  HOUSEHOLD: "Household",
  FRONT: "Front / checkout",
  OTHER: "Other",
};

export function formatPrice(price: number | null, unit?: string | null, currency = "USD") {
  if (price === null) return "Price unavailable";
  const amount = new Intl.NumberFormat(undefined, { style: "currency", currency }).format(price);
  return unit ? `${amount} / ${unit}` : amount;
}
